import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Chess } from "chess.js";
import { 
  Flag, 
  Clock,
  Loader2,
  Crown,
  User,
  Bot,
  ArrowLeft,
  List,
  ChevronLeft,
  ChevronRight,
  Download,
  Volume2,
  VolumeX
} from "lucide-react";
import { useChessSounds } from "@/hooks/useChessSounds";
import { API_URL } from "@/config/api";
import { GameBoard } from "@/components/chess/GameBoard";
import { GameClock } from "@/components/chess/GameClock";
import { MoveList, replayMoves } from "@/components/chess/MoveList";

export default function BotGamePage() {
  const { t } = useTranslation();
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useStore();
  
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [resigning, setResigning] = useState(false);
  const [makingMove, setMakingMove] = useState(false);
  const [requestingRematch, setRequestingRematch] = useState(false);
  const [moveHistory, setMoveHistory] = useState([]);
  const [pgnNotation, setPgnNotation] = useState("");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [localTimes, setLocalTimes] = useState({ player: 0, bot: 0 });
  const timesSyncedAtRef = useRef(Date.now());
  const [viewIndex, setViewIndex] = useState(null);
  const [botThinking, setBotThinking] = useState(false);
  const steps = useMemo(() => replayMoves(moveHistory), [moveHistory]);
  const liveStep = steps[steps.length - 1];
  
  // Sound effects
  const { sounds } = useChessSounds(soundEnabled);

  // Fetch game data
  const fetchGame = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/games/${gameId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGame(data);
        setLocalTimes({ player: data.player_time, bot: data.bot_time });
        timesSyncedAtRef.current = Date.now();
        // Fetch move history
        fetchMoves();
      } else {
        toast.error("Game not found");
        navigate("/lobby");
      }
    } catch (err) {
      toast.error("Failed to load game");
    } finally {
      setLoading(false);
    }
  }, [gameId, token, API_URL, navigate]);

  // Fetch move history
  const fetchMoves = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/games/${gameId}/moves`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMoveHistory(data.moves || []);
        setPgnNotation(data.pgn || "");
      }
    } catch (err) {
      console.error("Failed to fetch moves");
    }
  };

  // Auto-scroll disabled - was causing page jump
  // useEffect(() => {
  //   if (movesEndRef.current) {
  //     movesEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  //   }
  // }, [moveHistory]);

  useEffect(() => {
    fetchGame();
  }, [fetchGame]);

  useEffect(() => {
    if (viewIndex !== null && viewIndex >= steps.length - 1) setViewIndex(null);
  }, [steps.length]);

  const BOT_MOVE_DELAY_MS = 450;

  const applyBotResponse = (data, prevGame) => {
    setGame(data.game);
    setLocalTimes({ player: data.game.player_time, bot: data.game.bot_time });
    timesSyncedAtRef.current = Date.now();
    const botChess = new Chess(data.game.fen);
    if (data.game.status === "finished") {
      if (data.game.winner === prevGame.player_color) {
        sounds.victory();
        toast.success(t("game.youWin"));
      } else if (data.game.winner === "draw") {
        sounds.gameEnd();
        toast.info(t("game.drawResult"));
      } else {
        sounds.defeat();
        toast.info(t("game.youLose"));
      }
    } else if (botChess.isCheck()) {
      sounds.check();
    } else if (data.bot_move_san?.includes("x") || data.game.bot_move?.includes("x")) {
      sounds.capture();
    } else {
      sounds.move();
    }
    fetchMoves();
  };

  const handleMove = (move) => {
    if (makingMove || botThinking || game?.status !== "active" || viewIndex !== null) return false;

    const tempChess = new Chess(game.fen);
    let moveResult = null;
    try {
      moveResult = tempChess.move({ from: move.slice(0, 2), to: move.slice(2, 4), promotion: move[4] || undefined });
    } catch {
      moveResult = null;
    }
    if (!moveResult) {
      sounds.illegal();
      return false;
    }

    const prevGame = game;
    setGame((prev) => ({ ...prev, fen: tempChess.fen(), turn: prev.player_color === "white" ? "black" : "white" }));
    const elapsedSinceSync = (Date.now() - timesSyncedAtRef.current) / 1000;
    setLocalTimes((prev) => ({ ...prev, player: Math.max(0, prev.player - elapsedSinceSync) }));
    timesSyncedAtRef.current = Date.now();
    setMoveHistory((prev) => [...prev, move]);
    if (tempChess.isCheck()) sounds.check();
    else if (moveResult.san.includes("x")) sounds.capture();
    else sounds.move();

    setMakingMove(true);
    setBotThinking(true);
    const startedAt = Date.now();

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/bot/games/${gameId}/move`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ move })
        });
        const data = await res.json();
        if (res.ok && data.success) {
          const wait = Math.max(0, BOT_MOVE_DELAY_MS - (Date.now() - startedAt));
          setTimeout(() => {
            applyBotResponse(data, prevGame);
            setBotThinking(false);
          }, wait);
        } else {
          sounds.illegal();
          toast.error(data.detail || t("live.illegalMove"));
          fetchGame();
          setBotThinking(false);
        }
      } catch (err) {
        sounds.illegal();
        toast.error(t("live.moveError"));
        fetchGame();
        setBotThinking(false);
      } finally {
        setMakingMove(false);
      }
    })();
    return true;
  };

  // Handle resign
  const handleResign = async () => {
    setResigning(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/games/${gameId}/resign`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        sounds.defeat();
        toast.info(t("game.resignation"));
        fetchGame();
      } else {
        toast.error("Failed to resign");
      }
    } catch (err) {
      toast.error("Failed to resign");
    } finally {
      setResigning(false);
    }
  };

  // Export PGN
  const handleExportPGN = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/games/${gameId}/export-pgn`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `teachess_bot_${gameId.slice(0, 8)}.pgn`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t("game.pgnExported", "PGN экспортирован"));
      } else {
        toast.error(t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  // Handle rematch
  const handleRematch = async () => {
    setRequestingRematch(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/games/${gameId}/rematch`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(t("game.rematch"));
        navigate(`/bot-game/${data.game.id}`);
      } else {
        toast.error(data.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setRequestingRematch(false);
    }
  };

  // Game state
  const isMyTurn = game?.turn === game?.player_color;
  const isActive = game?.status === "active";
  const isFinished = game?.status === "finished";

  // Determine game result
  const getGameResult = () => {
    if (!isFinished || !game?.winner) return null;
    
    if (game.winner === "draw") {
      return { text: t("game.drawResult"), isWin: null };
    }
    
    const didWin = game.winner === game.player_color;
    return {
      text: didWin ? t("game.youWin") : t("game.youLose"),
      isWin: didWin
    };
  };

  const gameResult = getGameResult();

  const getDifficultyLabel = (diff) => {
    const levelNames = {
      1: "Новичок",
      2: "Начинающий",
      3: "Любитель", 
      4: "Игрок клуба",
      5: "Опытный",
      6: "Сильный",
      7: "Кандидат",
      8: "Мастер",
      9: "Гроссмейстер",
      10: "Чемпион"
    };
    const num = parseInt(String(diff).replace("level_", ""), 10);
    const named = { easy: 2, medium: 4, hard: 6, master: 8 }[diff];
    const level = Number.isNaN(num) ? named : num;
    const name = t(`game.levelNames.${level}`, levelNames[level]);
    return name ? `${t("game.level", "Уровень")} ${level} · ${name}` : `${t("game.level", "Уровень")} ${diff}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-4 px-1 sm:px-0" data-testid="bot-game-page">
      {/* Back button & Sound toggle */}
      <div className="flex justify-between items-center mb-1 sm:mb-2">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/lobby")}
          className="h-8 sm:h-10 text-sm"
          size="sm"
        >
          <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          {t("common.back")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="h-8 px-2"
          data-testid="sound-toggle"
        >
          {soundEnabled ? (
            <Volume2 className="h-4 w-4 text-primary" />
          ) : (
            <VolumeX className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-12 gap-2 sm:gap-4 lg:gap-6">
        {/* Main board area */}
        <div className="lg:col-span-8 space-y-2 sm:space-y-4">
          {/* Bot info (opponent) - compact on mobile */}
          <Card className="glass border-border/50">
            <CardContent className="flex items-center justify-between py-2 px-3 sm:py-3 sm:px-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Bot className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
                </div>
                <div>
                  <p className="font-semibold text-sm sm:text-base">
                    {t("game.botOpponent")} ({getDifficultyLabel(game?.difficulty)})
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground capitalize">
                    {game?.player_color === "white" ? t("game.black") : t("game.white")}
                  </p>
                </div>
              </div>
              <GameClock time={localTimes.bot} running={isActive && !isMyTurn} testId="bot-clock" />
            </CardContent>
          </Card>

          {/* Chess board */}
          <div className="relative">
            {/* Game over overlay */}
            {isFinished && gameResult && (
              <div className="absolute inset-0 z-10 bg-background/80 backdrop-blur-sm flex items-center justify-center rounded-lg">
                <div className="text-center space-y-3 sm:space-y-4 p-4">
                  <Crown className={`h-12 w-12 sm:h-16 sm:w-16 mx-auto ${gameResult.isWin ? "text-primary" : gameResult.isWin === false ? "text-destructive" : "text-muted-foreground"}`} />
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-black">{gameResult.text}</h2>
                  </div>
                  <div className="flex gap-2 sm:gap-3 justify-center">
                    <Button 
                      onClick={handleRematch} 
                      className="btn-gold h-9 sm:h-10 text-sm"
                      disabled={requestingRematch}
                      data-testid="rematch-btn"
                    >
                      {requestingRematch ? (
                        <Loader2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                      ) : null}
                      {t("game.rematch")}
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/lobby")} className="h-9 sm:h-10 text-sm">
                      {t("nav.lobby")}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {viewIndex !== null && (
              <button
                type="button"
                onClick={() => setViewIndex(null)}
                className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-background/95 border border-primary/50 text-xs font-medium text-primary shadow"
                data-testid="back-to-live"
              >
                {t("game.viewingHistory", "Просмотр хода")} {viewIndex}/{steps.length - 1} · {t("game.backToLive", "к партии")}
              </button>
            )}
            {botThinking && viewIndex === null && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background/85 border border-orange-500/40 text-[11px] text-orange-400 pointer-events-none">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("game.botThinking", "Бот думает...")}
              </div>
            )}

            <GameBoard
              id="bot-board"
              fen={viewIndex !== null ? steps[viewIndex].fen : (game?.fen || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1")}
              orientation={game?.player_color || "white"}
              myColor={game?.player_color || "white"}
              canMove={isMyTurn && isActive && !makingMove && !botThinking && viewIndex === null}
              onMove={handleMove}
              lastMove={viewIndex !== null ? steps[viewIndex] : liveStep}
              testIdPrefix="bot"
            />
          </div>

          {/* Player info - compact on mobile */}
          <Card className="glass border-border/50">
            <CardContent className="flex items-center justify-between py-2 px-3 sm:py-3 sm:px-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-sm sm:text-base">{user?.username} ({t("common.you")})</p>
                  <p className="text-xs sm:text-sm text-muted-foreground capitalize">
                    {game?.player_color === "white" ? t("game.white") : t("game.black")}
                  </p>
                </div>
              </div>
              <GameClock time={localTimes.player} running={isActive && isMyTurn} testId="player-clock" />
            </CardContent>
          </Card>
        </div>

        {/* Side panel - below board on mobile */}
        <div className="lg:col-span-4 space-y-2 sm:space-y-4">
          {/* Game info - compact on mobile */}
          <Card className="glass border-border/50 hidden sm:block">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                {t("game.practiceMode")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="text-muted-foreground">{t("game.selectDifficulty")}</span>
                <span className="font-semibold">{getDifficultyLabel(game?.difficulty)}</span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="text-muted-foreground">{t("game.timeControl")}</span>
                <span>{Math.floor((game?.time_control || 600) / 60)} min + {game?.increment || 0}s</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span className={`capitalize ${
                  isActive ? "text-green-500" : "text-muted-foreground"
                }`}>
                  {game?.status}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Mobile: Turn indicator + Resign in row */}
          <div className="flex gap-2 lg:hidden">
            {/* Turn indicator - mobile */}
            {isActive && (
              <Card className={`flex-1 border-2 ${isMyTurn ? "border-primary glow-gold" : "border-border/50"}`}>
                <CardContent className="py-2 sm:py-3 text-center">
                  <p className={`font-bold text-sm ${isMyTurn ? "text-primary" : "text-muted-foreground"}`}>
                    {isMyTurn ? t("game.yourTurn") : t("game.opponentTurn")}
                  </p>
                </CardContent>
              </Card>
            )}
            {/* Resign button - mobile */}
            {isActive && (
              <Button 
                variant="destructive" 
                className="h-auto py-2 sm:py-3"
                onClick={handleResign}
                disabled={resigning}
                data-testid="bot-resign-btn-mobile"
              >
                {resigning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Flag className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>

          {/* Desktop: Actions */}
          {isActive && (
            <Card className="glass border-border/50 hidden lg:block">
              <CardContent className="pt-4 space-y-2">
                <Button 
                  variant="destructive" 
                  className="w-full"
                  onClick={handleResign}
                  disabled={resigning}
                  data-testid="bot-resign-btn"
                >
                  {resigning ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Flag className="mr-2 h-4 w-4" />
                  )}
                  {t("game.resign")}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Desktop: Turn indicator */}
          {isActive && (
            <Card className={`hidden lg:block border-2 ${isMyTurn ? "border-primary glow-gold" : "border-border/50"}`}>
              <CardContent className="py-4 text-center">
                <p className={`font-bold ${isMyTurn ? "text-primary" : "text-muted-foreground"}`}>
                  {isMyTurn ? t("game.yourTurn") : t("game.opponentTurn")}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Move History - collapsible on mobile */}
          <Card className="glass border-border/50" data-testid="move-history-card">
            <CardHeader className="pb-2 py-2 sm:py-4">
              <CardTitle className="text-base sm:text-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <List className="h-4 w-4 text-primary" />
                  <span className="hidden sm:inline">{t("game.moveHistory", "История ходов")}</span>
                  <span className="sm:hidden text-sm">{t("game.moveHistory", "Ходы")}</span>
                </div>
                {moveHistory.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleExportPGN}
                    className="h-7 sm:h-8 px-2"
                    data-testid="export-pgn-btn"
                  >
                    <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="text-xs sm:text-sm">PGN</span>
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <MoveList steps={steps} viewIndex={viewIndex} onSelect={setViewIndex} height="h-40 sm:h-56" />
              {moveHistory.length > 0 && (
                <p className="text-xs text-muted-foreground text-center pt-2">
                  {t("game.totalMoves", "Всего ходов")}: {moveHistory.length}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
